import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// TopBar — the themed top bar of the AppShell: breadcrumb + actions + a
// notification bell (design/components/app-shell.html). The current crumb is
// not a link; the bell's unread dot is paired with its aria-label. On mobile a
// leading hamburger (onMenuClick) opens the nav drawer.

function HamburgerIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  )
}

export interface TopBarBreadcrumbItem {
  label: string
  href?: string
}

export interface TopBarProps {
  breadcrumb: TopBarBreadcrumbItem[]
  actions?: ReactNode
  hasNotifications?: boolean | undefined
  /** Numeric unread count: shows a count pill instead of the bare dot.
   *  The dot said "something, sometime"; the number says how much is waiting. */
  notificationCount?: number | undefined
  onBellClick?: (() => void) | undefined
  /** When set, a leading hamburger button appears (mobile nav trigger). */
  onMenuClick?: (() => void) | undefined
  className?: string
}

/**
 * Themed top bar of the AppShell: breadcrumb (current crumb unlinked), action
 * slot and notification bell. The bell renders only when `onBellClick` is
 * wired; `onMenuClick` adds the mobile hamburger that opens the nav drawer.
 */
export function TopBar({
  breadcrumb,
  actions,
  hasNotifications,
  notificationCount,
  onBellClick,
  onMenuClick,
  className,
}: TopBarProps) {
  const { t } = useTranslation('components')
  return (
    <div
      className={cn(
        'flex h-[55px] flex-none items-center gap-3 border-b border-line bg-surface px-[18px]',
        className,
      )}
    >
      {onMenuClick && (
        <button
          type="button"
          onClick={onMenuClick}
          aria-label={t('ui.openMenu')}
          className="-ml-1.5 flex size-10 flex-none items-center justify-center rounded-md text-text-2 transition-colors hover:bg-hover hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue lg:hidden [&_svg]:size-[19px]"
        >
          <HamburgerIcon />
        </button>
      )}

      <nav aria-label={t('ui.breadcrumb')}>
        <ol className="flex items-center gap-[7px] text-[12px] text-text-3">
          {breadcrumb.map((c, i) => {
            const last = i === breadcrumb.length - 1
            return (
              <li key={i} className="flex items-center gap-[7px]">
                {c.href && !last ? (
                  <a href={c.href} className="text-text-3 transition-colors hover:text-text-2">
                    {c.label}
                  </a>
                ) : (
                  <span
                    className={cn(last && 'font-semibold text-text')}
                    aria-current={last ? 'page' : undefined}
                  >
                    {c.label}
                  </span>
                )}
                {!last && (
                  <span aria-hidden className="opacity-50">
                    /
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      </nav>

      <div className="ml-auto flex items-center gap-2">
        {actions}
        {/* The bell only renders when wired — a notifications surface that does
            nothing is worse than no bell. Pass onBellClick to bring it back. */}
        {onBellClick && (
          <button
            type="button"
            onClick={onBellClick}
            aria-label={
              hasNotifications ? t('ui.topbar.notificationsUnread') : t('ui.topbar.notifications')
            }
            className="relative flex size-10 items-center justify-center rounded-md text-text-2 transition-colors hover:bg-hover hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue lg:size-8 [&_svg]:size-[17px]"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
              <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
            </svg>
            {notificationCount !== undefined && notificationCount > 0 ? (
              <span
                aria-hidden
                className="absolute -right-0.5 top-0.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-pill border-[1.5px] border-surface bg-sens px-[3px] text-[9px] font-bold leading-none text-white"
              >
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            ) : hasNotifications ? (
              <span
                aria-hidden
                className="absolute right-[7px] top-1.5 size-1.5 rounded-full border-[1.5px] border-surface bg-sens"
              />
            ) : null}
          </button>
        )}
      </div>
    </div>
  )
}
